import React, { useReducer } from 'react';
import { Button, Typography } from '@one-thd/sui-atomic-components';
import PropTypes from 'prop-types';
import {
  unsubscribeFromDirectMail,
  addressStandardization, hasPermissionToContact,
} from '../../common/sharedFunctions';
import '../../styles/UnSubModal.scss';
import './PostalMailPreferences.scss';
import { ConfirmAddressModal } from './ConfirmAddressModal';
import { InputAddressModal } from './InputAddressModal';
import { SelectAddressModal } from './SelectAddressModal';
import { addAnalyticEvent } from '../../analytics/common';
import {
  confirmPostalUnsub,
  unsubscribeFromPostalMail
} from '../../analytics/address';
import { BannerContext, BannerProvider } from '../../contexts/BannerContext';
import ErrorBoundary from '../../contexts/ErrorBoundary';
import bannerConstants from '../../constants/bannerConstants';

const Actions = {
  OPEN_INPUT_MODAL: 'OPEN_INPUT_MODAL',
  TRY_AGAIN: 'TRY_AGAIN',
  OPEN_CONFIRM_ADDRESS_MODAL: 'OPEN_CONFIRM_ADDRESS_MODAL',
  OPEN_SELECT_ADDRESS_MODAL: 'OPEN_SELECT_ADDRESS_MODAL',
  SUCCESSFULLY_UNSUBSCRIBED: 'SUCCESSFULLY_UNSUBSCRIBED',
  RESET: 'RESET',
};

const initialState = () => {
  return {
    address: {},
    verifiedAddress: {},
    useVerifiedAddress: false,
    openInputModal: false,
    openConfirmAddressModal: false,
    openSelectAddressModal: false
  };
};

function Preferences({ isMobile, isSearchedInitiated }) {

  const {
    openSuccessAlert,
    openErrorAlert,
    hideBanners
  } = React.useContext(BannerContext);

  const reducer = (oldState, action) => {
    switch (action.type) {
    case Actions.TRY_AGAIN:
      return {
        ...initialState(),
        openInputModal: true,
      };
    case Actions.OPEN_INPUT_MODAL:
      hideBanners();
      return {
        ...oldState,
        openInputModal: true
      };
    case Actions.OPEN_CONFIRM_ADDRESS_MODAL:
      return {
        ...oldState,
        openInputModal: false,
        openConfirmAddressModal: true,
        address: action.payload.address,
        useVerifiedAddress: action.payload.useVerifiedAddress,
        verifiedAddress: action.payload.verifiedAddress,
      };
    case Actions.OPEN_SELECT_ADDRESS_MODAL:
      return {
        ...oldState,
        useVerifiedAddress: false,
        openSelectAddressModal: true,
        openConfirmAddressModal: false,
      };
    case Actions.SUCCESSFULLY_UNSUBSCRIBED:
      openSuccessAlert(bannerConstants.POSTAL_OPT_OUT_CONFIRMATION, action.payload.bannerAddress);
      return {
        ...oldState,
        openConfirmAddressModal: false,
        openSelectAddressModal: false,
        bannerAddress: action.payload.bannerAddress
      };
    case Actions.RESET:
      return initialState();
    default:
      return oldState;
    }
  };

  const [state, dispatch] = useReducer(reducer, null, initialState);

  const ignoreError = (err) => {};

  const handleContinue = (addr) => {
    let verifiedAddress;
    let useVerifiedAddress;

    addressStandardization(addr, (response) => {
      const data = response.data;

      if (!data.addressLine1) {
        useVerifiedAddress = false;
      } else {
        useVerifiedAddress = true;
        verifiedAddress = {
          ...addr,
          addressLine1: data?.addressLine1,
          addressLine2: (data?.addressLine2 || ''),
          city: data?.city || addr.city,
          standardized: true
        };
      }

      dispatch({
        type: Actions.OPEN_CONFIRM_ADDRESS_MODAL,
        payload: {
          address: addr,
          verifiedAddress,
          useVerifiedAddress,
        },
      });
    }, ignoreError);
  };

  const callUnsubscribe = (address) => {
    unsubscribeFromDirectMail(address, (response) => {
      if (!hasPermissionToContact(response)) {
        addAnalyticEvent(confirmPostalUnsub);
        dispatch({
          type: Actions.SUCCESSFULLY_UNSUBSCRIBED,
          payload: { bannerAddress: address }
        });
      }
    }, () => {
      openErrorAlert();
      dispatch({ type: Actions.RESET });
    });
  };

  const unsubscribeVerifiedAddress = () => {
    callUnsubscribe(state.verifiedAddress);
  };

  const unsubscribeAddress = () => {
    callUnsubscribe(state.address);
  };

  const handleClose = () => {
    dispatch({ type: Actions.RESET });
  };

  const openInputModal = () => {
    addAnalyticEvent(unsubscribeFromPostalMail);
    dispatch({ type: Actions.OPEN_INPUT_MODAL });
  };

  return (
    <div className="sui-grid sui-gap-4">
      <Typography variant="h2" weight="display">POSTAL MAIL PREFERENCES</Typography>
      <Typography variant="body-base" height="none">
        The Home Depot occasionally will send
        information about products, services, special offers and other promotions by mail.
        If you do not want to receive promotional mail from The Home Depot, please click on the
        &quot;Unsubscribe From Postal Mail&quot; button below to be removed from the mailing list.
        Our mailing processes typically take 6-8 weeks from initiation through delivery.
        Please allow this time for the opt out to take full effect.
      </Typography>

      <div className="sui-grid sui-grid-cols-8">
        <div className="hidden sm:sui-col-span-4 md:sui-col-span-5" />
        <div className="sui-col-span-8 sm:sui-col-span-4 md:sui-col-span-3 sui-grid sui-gap-y-4">
          <Button
            aria-label="optOutPostalMailButton"
            fullWidth
            variant="secondary"
            onClick={openInputModal}
          >
            Opt Out of Postal Mail
          </Button>
        </div>
      </div>

      {state.openInputModal && (
        <InputAddressModal
          isMobile={isMobile}
          handleClose={handleClose}
          handleContinue={handleContinue}
        />
      )}

      {state.openConfirmAddressModal && (
        <ConfirmAddressModal
          isMobile={isMobile}
          onClose={handleClose}
          unsubscribeAddress={unsubscribeAddress}
          unsubscribeVerifiedAddress={unsubscribeVerifiedAddress}
          useVerifiedAddress={state.useVerifiedAddress}
          address={state.useVerifiedAddress ? state.verifiedAddress : state.address}
          confirmUseUnverified={() => dispatch({ type: Actions.OPEN_SELECT_ADDRESS_MODAL })}
          tryAgainButton={() => dispatch({ type: Actions.TRY_AGAIN })}
        />
      )}

      {state.openSelectAddressModal && (
        <SelectAddressModal
          isMobile={isMobile}
          handleClose={handleClose}
          address={state.address}
          verifiedAddress={state.verifiedAddress}
          useVerifiedAddress={state.useVerifiedAddress}
          openAddressEntryModal={() => dispatch({ type: Actions.TRY_AGAIN })}
          unsubscribeAddress={unsubscribeAddress}
          unsubscribeVerifiedAddress={unsubscribeVerifiedAddress}
        />
      )}
    </div>
  );
}

function PostalMailPreferences({ isMobile, isSearchedInitiated }) {
  return (
    <ErrorBoundary>
      <BannerProvider>
        <Preferences isMobile={isMobile} isSearchedInitiated={isSearchedInitiated} />
      </BannerProvider>
    </ErrorBoundary>
  );
}

const propTypes = {
  isMobile: PropTypes.bool.isRequired,
  isSearchedInitiated: PropTypes.bool,
};

const defaultProps = {
  isSearchedInitiated: false,
};

PostalMailPreferences.propTypes = propTypes;
Preferences.propTypes = propTypes;

PostalMailPreferences.defaultProps = defaultProps;
Preferences.defaultProps = defaultProps;

export default PostalMailPreferences;
